import { useState, useEffect, useRef, useCallback } from 'react';

// Custom hook for scroll animations
export const useScrollAnimation = (threshold = 0.1, rootMargin = '0px') => {
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Disconnect observer after animation triggered
          observer.disconnect();
        }
      },
      { threshold, rootMargin }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      if (elementRef.current) {
        observer.unobserve(elementRef.current);
      }
    };
  }, [threshold, rootMargin]);

  return [elementRef, isVisible];
};

// Hook for multiple elements with staggered animations
export const useScrollAnimationGroup = (threshold = 0.1) => {
  const [visibleItems, setVisibleItems] = useState(new Set());
  const observerRef = useRef(null);
  const elementsRef = useRef(new Map());

  const setRef = useCallback((id) => (element) => {
    if (element) {
      elementsRef.current.set(id, element);
      observerRef.current?.observe(element);
    }
  }, []);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = [...elementsRef.current.entries()]
              .find(([, el]) => el === entry.target)?.[0];
            if (id !== undefined) {
              setVisibleItems((prev) => new Set([...prev, id]));
            }
          }
        });
      },
      { threshold }
    );

    elementsRef.current.forEach((element) => {
      observerRef.current?.observe(element);
    });

    return () => observerRef.current?.disconnect();
  }, [threshold]);

  const isVisible = useCallback((id) => visibleItems.has(id), [visibleItems]);

  return { setRef, isVisible };
};

// Animated number counter component
export const AnimatedNumber = ({ value, isVisible, duration = 2000 }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (isVisible) {
      let start = 0;
      // Handle both numeric values and strings with suffixes
      const numericValue = typeof value === 'string' ? 
        parseFloat(value.replace(/[^\d.-]/g, '')) || 0 : 
        value;
      
      const increment = numericValue / (duration / 16); // 60fps approximation

      const timer = setInterval(() => {
        start += increment;
        if (start >= numericValue) {
          clearInterval(timer);
          setCount(numericValue);
        } else {
          setCount(Math.ceil(start));
        }
      }, 16);

      return () => clearInterval(timer);
    }
  }, [isVisible, value, duration]);

  // Handle string values with suffixes (like "14+" or "70%")
  if (typeof value === 'string') {
    const numericPart = parseFloat(value.replace(/[^\d.-]/g, '')) || 0;
    const suffix = value.replace(/[\d.-]/g, '');
    return <>{count}{suffix}</>;
  }

  return <>{count}</>;
};

export default useScrollAnimation;